import { MessageSquare } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { PictureAvatar } from "@/features/user-avatar";
import { ApiError } from "@/lib/api";
import { statusLine } from "@/lib/chats";
import { useOpenDirectChat, usePersonStatus } from "@/lib/queries";
import type { ChatPerson } from "@/lib/types";
import { cn } from "@/lib/utils";
import { AVAILABILITY_DOT } from "@/softphone/availability";

/**
 * A person in a chat as a card over their name (platform ADR-186): their picture, name, role and
 * extension, where they are on the phone now ("Status": read while the card is open), and Message,
 * which opens the direct chat with them (made the first time either opens it). A popover rather
 * than a tooltip, since it holds a button. The card opens as the pointer rests on the name and closes once it has left both the name
 * and the card; a press or Enter opens it too, for a touch screen or a keyboard.
 */

const OPEN_MS = 250;
const CLOSE_MS = 200;

export function ChatPersonCard({
  person, detail, canMessage, variant = "row", side = "left", onOpenChat, className, children,
}: {
  person: ChatPerson;
  /** Under the name: their role and extension. */
  detail: string;
  /** Whether the card offers Message: not for oneself, somebody deactivated, or the person a
   *  direct chat on screen is already with (`lib/chats.ts::messageable`). */
  canMessage: boolean;
  /** A row of the people list (picture and name), or a name inline in a message. */
  variant?: "row" | "inline";
  side?: "left" | "bottom";
  /** Where Message opens the direct chat; the Team Chat page when not given. */
  onOpenChat?: (chatId: number) => void;
  className?: string;
  /** What the name looks like where it is: the trigger. */
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const start = useOpenDirectChat();
  const [open, setOpen] = React.useState(false);
  const status = usePersonStatus(person.user_id, open);
  const line = status.data ? statusLine(status.data) : null;
  const timer = React.useRef<number | undefined>(undefined);
  // Opened by the pointer resting on the name, focus stays where it is; by a press or Enter, it
  // moves into the card so Message is one Tab away.
  const byHover = React.useRef(false);

  const later = (next: boolean, ms: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(next), ms);
  };
  React.useEffect(() => () => window.clearTimeout(timer.current), []);

  const message = async () => {
    try {
      const chat = await start.mutateAsync(person.user_id);
      setOpen(false);
      if (onOpenChat) onOpenChat(chat.id);
      else navigate(`/messages/${chat.id}`);
    } catch (e) {
      toast.error("Could not open the chat", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8",
            variant === "row"
              ? cn(
                  "flex w-full items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left transition-colors",
                  "hover:bg-surface-hover",
                  open && "bg-surface-hover",
                )
              : cn("rounded-sm text-left hover:underline", open && "underline"),
            className,
          )}
          onPointerEnter={(e) => {
            if (e.pointerType !== "mouse") return;
            byHover.current = true;
            later(true, OPEN_MS);
          }}
          onPointerLeave={(e) => e.pointerType === "mouse" && later(false, CLOSE_MS)}
          onClick={(e) => {
            // A press opens it and keeps it open, rather than closing a card the pointer opened.
            e.preventDefault();
            window.clearTimeout(timer.current);
            byHover.current = false;
            setOpen(true);
          }}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align="start"
        aria-label={person.name ?? "A person"}
        className="w-64 p-3"
        onPointerEnter={(e) => e.pointerType === "mouse" && window.clearTimeout(timer.current)}
        onPointerLeave={(e) => e.pointerType === "mouse" && later(false, CLOSE_MS)}
        onOpenAutoFocus={(e) => byHover.current && e.preventDefault()}
      >
        <div className="flex items-center gap-3">
          <PictureAvatar name={person.name} url={person.avatar_url} className="size-12" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-text">{person.name ?? "Someone"}</p>
            <p className="truncate text-xs text-text-muted">{detail}</p>
            {line && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-text">
                <span className={cn("size-2 shrink-0 rounded-full", AVAILABILITY_DOT[line.tone])} aria-hidden />
                <span className="truncate">{line.label}</span>
              </p>
            )}
          </div>
        </div>
        {canMessage && (
          <div className="mt-2.5 flex justify-end border-t border-border pt-2.5">
            <Button size="sm" variant="outline" loading={start.isPending} onClick={message}>
              <MessageSquare />
              Message
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
